import { useId, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { AddressCountryPicker } from "@/components/crm/AddressCountryPicker";
import { CrmSelect, crmSelectFieldWrapperClass } from "@/components/crm/CrmListChevron";
import { addressFormFields } from "@/lib/address/addressForm";
import { subdivisionsForCountry } from "@/lib/address/subdivisions";
import {
  appendAddressRow,
  coerceAddressRowType,
  displayCrmAddressTypeLabel,
  emptyAddressRow,
  firstInvalidCrmAddress,
  formatCrmAddressTypeLabel,
  mergeAddressTypesForEditor,
  removeAddressAtIndex,
  setAddressPrimaryAtIndex,
  type CrmAddressTypeOptions,
  type CrmContactAddress,
} from "@/lib/crmContactAddresses";

const fieldClass =
  "w-full rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-white/20 dark:bg-black/20";
const selectTextClass = "bg-transparent py-2 pl-3 text-sm";

type CrmContactAddressesEditorProps = {
  addresses: CrmContactAddress[];
  onChange: (next: CrmContactAddress[]) => void;
  disabled?: boolean;
  addressTypeOptions?: CrmAddressTypeOptions;
  defaultCountryId?: string;
};

export function CrmContactAddressesEditor({
  addresses,
  onChange,
  disabled,
  addressTypeOptions,
  defaultCountryId = "us",
}: CrmContactAddressesEditorProps) {
  const primaryGroup = useId();
  const [touched, setTouched] = useState<Record<number, boolean>>({});

  const baseTypes = addressTypeOptions?.addressTypes.length
    ? addressTypeOptions.addressTypes
    : (["billing", "shipping", "headquarters", "home"] as const);
  const typeOptionsList = mergeAddressTypesForEditor([...baseTypes], addresses);

  const typeLabel = (type: CrmContactAddress["type"]) =>
    displayCrmAddressTypeLabel(addressTypeOptions?.addressLabels[type], type);

  const rowError = (index: number, row: CrmContactAddress): string | null => {
    if (!touched[index]) return null;
    const invalid = firstInvalidCrmAddress([row]);
    return invalid?.message ?? null;
  };

  const updateRow = (index: number, patch: Partial<CrmContactAddress>) => {
    const next = addresses.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Address</h3>
        <button
          type="button"
          disabled={disabled}
          onClick={() =>
            onChange(
              appendAddressRow(
                addresses,
                emptyAddressRow(
                  defaultCountryId,
                  false,
                  coerceAddressRowType(typeOptionsList[0] || "billing", addressTypeOptions ?? {
                    addressTypes: [...baseTypes],
                    addressLabels: {},
                  }),
                ),
              ),
            )
          }
          className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:underline disabled:opacity-50 dark:text-indigo-300"
        >
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Add address
        </button>
      </div>

      {addresses.length === 0 ? (
        <p className="text-xs text-slate-500 dark:text-slate-400">No addresses yet.</p>
      ) : (
        addresses.map((row, index) => {
          const error = rowError(index, row);
          const fields = addressFormFields(row.countryCode || defaultCountryId);
          const subdivisions = subdivisionsForCountry(row.countryCode || defaultCountryId);
          const options =
            addressTypeOptions ??
            ({
              addressTypes: [...baseTypes],
              addressLabels: {},
            } satisfies CrmAddressTypeOptions);
          const rowType = coerceAddressRowType(row.type, options);

          return (
            <div
              key={`address-${index}`}
              className={`space-y-3 rounded-lg border p-3 ${
                error ? "border-red-400 dark:border-red-500/70" : "border-gray-200 dark:border-white/10"
              }`}
            >
              <div className="flex flex-wrap items-center gap-2">
                <CrmSelect
                  wrapperClassName={`basis-full ${crmSelectFieldWrapperClass}`}
                  disabled={disabled}
                  value={rowType}
                  onChange={(e) =>
                    updateRow(index, {
                      type: coerceAddressRowType(
                        e.target.value as CrmContactAddress["type"],
                        options,
                      ),
                    })
                  }
                  className={selectTextClass}
                  aria-label="Address type"
                >
                  {typeOptionsList.map((t) => (
                    <option key={t} value={t}>
                      {typeLabel(t)}
                    </option>
                  ))}
                </CrmSelect>
                <label className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                  <input
                    type="radio"
                    name={primaryGroup}
                    disabled={disabled}
                    checked={Boolean(row.isPrimary)}
                    onChange={() => onChange(setAddressPrimaryAtIndex(addresses, index))}
                  />
                  Primary
                </label>
                <button
                  type="button"
                  disabled={disabled}
                  aria-label="Remove address"
                  onClick={() => onChange(removeAddressAtIndex(addresses, index))}
                  className="ml-auto rounded-md border border-gray-300 p-2 text-gray-600 hover:bg-gray-50 dark:border-white/20 dark:text-gray-300"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {fields.map((spec) => {
                  if (spec.id === "country") {
                    return (
                      <label key={`${index}-country`} className="text-xs font-medium text-slate-600 sm:col-span-2 dark:text-slate-300">
                        {spec.label}
                        <div className="mt-1">
                          <AddressCountryPicker
                            value={row.countryCode || defaultCountryId}
                            disabled={disabled}
                            onChange={(code) => updateRow(index, { countryCode: code })}
                          />
                        </div>
                      </label>
                    );
                  }

                  if (spec.id === "adminArea" && spec.adminAreaSelect && subdivisions.length) {
                    const states = subdivisions.filter((s) => !s.military && !s.territory);
                    const territories = subdivisions.filter((s) => s.territory);
                    const military = subdivisions.filter((s) => s.military);
                    const flatList = territories.length === 0 && military.length === 0;
                    return (
                      <label key={`${index}-admin`} className="text-xs font-medium text-slate-600 dark:text-slate-300">
                        {spec.label}
                        <CrmSelect
                          wrapperClassName={`mt-1 ${crmSelectFieldWrapperClass}`}
                          disabled={disabled}
                          value={row.adminArea || ""}
                          onChange={(e) => updateRow(index, { adminArea: e.target.value })}
                          className={selectTextClass}
                        >
                          <option value="">Select…</option>
                          {flatList
                            ? states.map((s) => (
                                <option key={s.code} value={s.code}>
                                  {s.name}
                                </option>
                              ))
                            : null}
                          {!flatList ? (
                            <>
                              <optgroup label="States">
                                {states.map((s) => (
                                  <option key={s.code} value={s.code}>
                                    {s.name}
                                  </option>
                                ))}
                              </optgroup>
                              {territories.length ? (
                                <optgroup label="Territories">
                                  {territories.map((s) => (
                                    <option key={s.code} value={s.code}>
                                      {s.name}
                                    </option>
                                  ))}
                                </optgroup>
                              ) : null}
                              {military.length ? (
                                <optgroup label="Military (APO/FPO/DPO)">
                                  {military.map((s) => (
                                    <option key={s.code} value={s.code}>
                                      {s.name}
                                    </option>
                                  ))}
                                </optgroup>
                              ) : null}
                            </>
                          ) : null}
                        </CrmSelect>
                      </label>
                    );
                  }

                  const valueKey = spec.id as keyof CrmContactAddress;
                  const value = String(row[valueKey] ?? "");
                  return (
                    <label
                      key={`${index}-${spec.id}`}
                      className={`text-xs font-medium text-slate-600 dark:text-slate-300${
                        spec.id === "line1" || spec.id === "line2" ? " sm:col-span-2" : ""
                      }`}
                    >
                      {spec.label}
                      <input
                        disabled={disabled}
                        value={value}
                        onChange={(e) => updateRow(index, { [valueKey]: e.target.value } as Partial<CrmContactAddress>)}
                        onBlur={() => setTouched((prev) => ({ ...prev, [index]: true }))}
                        placeholder={spec.placeholder}
                        className={`${fieldClass} mt-1`}
                      />
                    </label>
                  );
                })}
              </div>
              {error ? <p className="text-xs text-red-600 dark:text-red-400">{error}</p> : null}
            </div>
          );
        })
      )}
    </div>
  );
}

export { formatCrmAddressTypeLabel };
